<script setup lang="ts">
import { getAliasResolve, getLicenseChangeLog, getLicenseDetail, getTraderDetail } from '~/server/detail'
import { getRegulatorDisclosureDetail } from '~/api/regulator-disclosure'
import { getPostFeedbackSetting } from '~/server/exchange-detail'

definePageMeta({
  name: 'exchange-licenses',
  title: 'certificate',
  layout: false,
  hideDefaultTopNav: true,
})

const route = useRoute()
const { t } = useAppI18n()
const { isRtl } = useRtl()

const certificateFallbackMessages: Record<string, string> = {
  '76684': '{BrokerName} - {Regulator} Certificate',
  '77024': 'View the certificate files for {BrokerName} under {Regulator}',
}
const translate = (key: string) => {
  const translated = t(key)

  return translated === key ? certificateFallbackMessages[key] || key : translated
}
const normalizeText = (value: unknown) => {
  if (Array.isArray(value)) {
    return value[0] ? String(value[0]).trim() : ''
  }

  return value === null || value === undefined ? '' : String(value).trim()
}
const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) return normalizeText(value[0])
  return normalizeText(value)
}
const normalizeStringList = (value: unknown) => {
  if (!Array.isArray(value)) return []
  return value.map((item) => normalizeText(item)).filter(Boolean)
}
const normalizeChangeLogItems = (response: any) => {
  const result =
    response?.wikifxagg?.regulatorregulationchangelog ||
    response?.wikifxagg?.regulatorregulationchangelogv2 ||
    response?.data ||
    response
  const data = result?.data ?? result

  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(result?.items)) return result.items

  return []
}

const certificateType = computed(() => normalizeText(route.query.type) || '1')
const certificateFrom = computed(() => normalizeText(route.query.from))
const disclosureSlug = computed(() => normalizeText(route.query.slug))
const regulationId = computed(() => normalizeText(route.query.regulationId))
const projectCode = computed(() => normalizeText(route.query.projectCode))
const previewImageIndex = ref(0)
const previewImageSrc = ref('')
const queryImageIndex = normalizeText(route.query.imageIndex)
const queryImageSrc = normalizeText(route.query.imageSrc)
if (queryImageIndex) {
  const value = Number.parseInt(queryImageIndex, 10)
  previewImageIndex.value = Number.isNaN(value) || value < 0 ? 0 : value
}
if (queryImageSrc) {
  previewImageSrc.value = queryImageSrc
}
if (import.meta.client) {
  const stored =
    queryImageIndex ||
    window.sessionStorage.getItem('exampleImageIndex') ||
    window.localStorage.getItem('exampleImageIndex')
  const storedImageSrc =
    queryImageSrc || window.sessionStorage.getItem('exampleImageSrc') || window.localStorage.getItem('exampleImageSrc')
  if (stored !== null) {
    window.sessionStorage.removeItem('exampleImageIndex')
    window.localStorage.removeItem('exampleImageIndex')
    const value = Number.parseInt(stored, 10)
    previewImageIndex.value = Number.isNaN(value) || value < 0 ? 0 : value
  }
  if (storedImageSrc !== null) {
    window.sessionStorage.removeItem('exampleImageSrc')
    window.localStorage.removeItem('exampleImageSrc')
    previewImageSrc.value = storedImageSrc
  }
}
const exchangeSlug = computed(() => getFirstRouteValue(route.params.slug))
const { data: aliasResolveData } = await useAsyncData(
  () => `license-certificate-alias-resolve-${exchangeSlug.value}`,
  async () => {
    if (!exchangeSlug.value) return null
    return await getAliasResolve({ alias: exchangeSlug.value }).catch(() => null)
  },
  {
    watch: [exchangeSlug],
  },
)
const resolvedProjectCode = computed(() => {
  if (projectCode.value) return projectCode.value
  const items = (aliasResolveData.value?.data as Record<string, any> | undefined)?.items
  const resolvedCode = items?.find((item: any) => item.platform === 2)?.traderCode

  return normalizeText(resolvedCode || exchangeSlug.value)
})
const regulationIndex = computed(() => {
  const value = Number.parseInt(normalizeText(route.query.idx), 10)

  return Number.isNaN(value) ? -1 : value
})

const { data: certificateData } = await useAsyncData(
  () =>
    `license-certificate-${certificateFrom.value}-${disclosureSlug.value}-${certificateType.value}-${regulationId.value}-${resolvedProjectCode.value}-${regulationIndex.value}`,
  async () => {
    if (certificateFrom.value === 'disclosures-detail') {
      const detail = await getRegulatorDisclosureDetail(disclosureSlug.value).catch(() => null)
      const trader = Array.isArray(detail?.traders) ? detail.traders[0] : null

      return {
        imageList: normalizeStringList(detail?.imgs),
        brokerName: normalizeText(trader?.showName || trader?.shortName || trader?.localShortName),
        regulatorName: normalizeText(detail?.regInfo?.fullName || detail?.regInfo?.shortName),
      }
    }
    if (certificateFrom.value === 'Example') {
      const data = await getPostFeedbackSetting()
      return {
        imageList: normalizeStringList(data?.data?.samplePicture),
        brokerName: '',
        regulatorName: '',
      }
    }
    if (!resolvedProjectCode.value || (certificateFrom.value !== 'regulation-card' && !regulationId.value)) {
      return {
        imageList: [] as string[],
        brokerName: '',
        regulatorName: '',
      }
    }

    if (certificateFrom.value === 'regulation-card') {
      const traderDetail = await getTraderDetail(resolvedProjectCode.value).catch(() => null)
      const businessInfo = (traderDetail as any)?.license?.businessInfo || {}
      const basicInfo = (traderDetail as any)?.basicInfo || {}

      return {
        imageList: normalizeStringList(businessInfo.supportDocs),
        brokerName: normalizeText(basicInfo.showName || basicInfo.shortName || basicInfo.shortNameLocal),
        regulatorName: normalizeText(businessInfo.title),
      }
    }

    const [changeLogResponse, detailResponse] = await Promise.all([
      regulationIndex.value >= 0 ? getLicenseChangeLog(regulationId.value).catch(() => null) : Promise.resolve(null),
      getLicenseDetail(resolvedProjectCode.value, regulationId.value).catch(() => null),
    ])

    const changeLogItems = normalizeChangeLogItems(changeLogResponse)
    const detail = detailResponse || {}
    let imageList: string[] = []

    if (regulationIndex.value >= 0) {
      const attachments = changeLogItems?.[regulationIndex.value]?.attachments || []
      imageList = attachments.map((item: any) => normalizeText(item?.path || item)).filter(Boolean)
    } else {
      imageList = (detail as any)?.attachments || []
    }

    if (imageList.length === 0) {
      imageList = (detail as any)?.attachments || []
    }

    return {
      imageList,
      brokerName: normalizeText((detail as any)?.relatedTrader?.traderName),
      regulatorName: normalizeText((detail as any)?.header?.name),
    }
  },
  {
    watch: [certificateFrom, disclosureSlug, certificateType, regulationId, resolvedProjectCode, regulationIndex],
  },
)

const imageList = computed(() => certificateData.value?.imageList || [])
const brokerName = computed(() => certificateData.value?.brokerName || '')
const regulatorName = computed(() => certificateData.value?.regulatorName || '')

const imgIndex = ref(0)
const currentCarouselImage = computed(() => imageList.value[imgIndex.value] || '')
const ImgWidth = ref(134)
const imgHeight = ref(75)
const ImgMl = ref(8)
const isBusiness = ref(false)
const dialogVisible = ref(false)
const currentImage = ref('')
const scrollTimer = ref<number | null>(null)
const screenSize = ref<'xl' | 'lg' | 'md' | 'sm'>('xl')
const viewportWidth = ref(0)
const imgBox = ref<HTMLElement | null>(null)
const currentImageRef = ref<HTMLImageElement | null>(null)

const isMobile = computed(() => viewportWidth.value <= 799)
const visibleThumbnailCount = computed(() => {
  if (screenSize.value === 'md') return 5
  if (screenSize.value === 'lg') return 6
  return 8
})
const hasThumbnailOverflow = computed(() => imageList.value.length > visibleThumbnailCount.value)

const setCarouselImage = (index: number) => {
  if (!imageList.value.length) {
    imgIndex.value = 0
    return
  }

  imgIndex.value = Math.min(Math.max(index, 0), imageList.value.length - 1)
}

const getScreenSize = () => {
  const width = window.innerWidth

  if (width > 1199) return 'xl'
  if (width > 999) return 'lg'
  if (width > 799) return 'md'
  return 'sm'
}
const applyScreenSize = (size: 'xl' | 'lg' | 'md' | 'sm') => {
  if (size === 'xl') {
    ImgWidth.value = 134
    imgHeight.value = 75
  } else if (size === 'lg') {
    ImgWidth.value = 100
    imgHeight.value = 56
  } else if (size === 'md') {
    ImgWidth.value = 81
    imgHeight.value = 45
  }
}
const updateViewport = () => {
  viewportWidth.value = window.innerWidth
  screenSize.value = getScreenSize()
  applyScreenSize(screenSize.value)
  nextTick(() => updateScrollPosition())
}

const updateScrollPosition = () => {
  if (isMobile.value || !imgBox.value) return
  const sw = ImgMl.value + ImgWidth.value
  const visibleCount = visibleThumbnailCount.value

  if (imageList.value.length <= visibleCount) {
    imgBox.value.style.marginLeft = ''
    imgBox.value.style.marginRight = ''
    imgBox.value.style.transition = 'all 0.5s ease 0.1s'
    return
  }

  let targetMargin = 0
  const scrollStartIndex = Math.floor(visibleCount / 2)

  if (imgIndex.value >= scrollStartIndex) {
    const scrollDistance = imgIndex.value - scrollStartIndex
    const maxScrollDistance = imageList.value.length - visibleCount
    const actualScrollDistance = Math.min(scrollDistance, maxScrollDistance)
    targetMargin = -actualScrollDistance * sw
  }

  if (isRtl.value) {
    imgBox.value.style.marginRight = `${targetMargin}px`
    imgBox.value.style.marginLeft = ''
  } else {
    imgBox.value.style.marginLeft = `${targetMargin}px`
    imgBox.value.style.marginRight = ''
  }
  imgBox.value.style.transition = 'all 0.5s ease 0.1s'
}
const prev = () => {
  if (imgIndex.value === 0) {
    setCarouselImage(imageList.value.length - 1)
    updateScrollPosition()
    return
  }

  setCarouselImage(imgIndex.value - 1)
  updateScrollPosition()
}
const next = () => {
  if (imgIndex.value === imageList.value.length - 1) {
    setCarouselImage(0)
    return
  }

  setCarouselImage(imgIndex.value + 1)
  updateScrollPosition()
}
const prevImg = () => {
  if (imgIndex.value === 0) return
  prev()
}
const nextImg = () => {
  if (imgIndex.value === imageList.value.length - 1) return
  next()
}
const switchImg = (index: number) => {
  setCarouselImage(index)
  updateScrollPosition()
}
const openImg = (imgUrl = '') => {
  currentImage.value = imgUrl
  dialogVisible.value = true
  window.setTimeout(() => {
    handleImageLoad()
  }, 300)
}
const resetScroll = () => {
  nextTick(() => {
    const overflowBox = document.querySelector('.el-dialog__body') as HTMLElement | null

    if (!overflowBox) return
    overflowBox.scrollTop = 0
    overflowBox.scrollLeft = 0
  })
}
const handleScroll = () => {
  const overflowBox = document.querySelector('.el-dialog__body') as HTMLElement | null

  if (!overflowBox) return

  overflowBox.classList.add('scrolling')
  if (scrollTimer.value) window.clearTimeout(scrollTimer.value)
  scrollTimer.value = window.setTimeout(() => {
    overflowBox.classList.remove('scrolling')
  }, 500)
}
const handleImageLoad = () => {
  const img = currentImageRef.value

  if (!img) {
    window.setTimeout(() => handleImageLoad(), 100)
    return
  }

  const naturalWidth = img.naturalWidth
  const naturalHeight = img.naturalHeight

  if (naturalWidth <= 0 || naturalHeight <= 0) {
    window.setTimeout(() => handleImageLoad(), 100)
    return
  }

  img.style.width = `${naturalWidth}px`
  img.style.height = `${naturalHeight}px`
  img.style.aspectRatio = `${naturalWidth} / ${naturalHeight}`
  resetScroll()

  nextTick(() => {
    const overflowBox = document.querySelector('.el-dialog__body') as HTMLElement | null

    if (overflowBox) overflowBox.addEventListener('scroll', handleScroll)
  })
}
const cleanupPreviewScroll = () => {
  const overflowBox = document.querySelector('.el-dialog__body') as HTMLElement | null

  if (overflowBox) overflowBox.removeEventListener('scroll', handleScroll)
  if (scrollTimer.value) {
    window.clearTimeout(scrollTimer.value)
    scrollTimer.value = null
  }
}

watch(
  [imageList, previewImageIndex, previewImageSrc],
  ([value]) => {
    if (!value.length) {
      setCarouselImage(0)
      return
    }

    const previewIndex = previewImageSrc.value ? value.findIndex((item) => item === previewImageSrc.value) : -1
    setCarouselImage(previewIndex >= 0 ? previewIndex : previewImageIndex.value)
    nextTick(() => updateScrollPosition())
  },
  { immediate: true },
)
watch(dialogVisible, (value) => {
  if (!value) cleanupPreviewScroll()
})

useHead(() => ({
  title: translate('76684').replace('{BrokerName}', brokerName.value).replace('{Regulator}', regulatorName.value),
  meta: [
    {
      name: 'description',
      content: translate('77024').replace('{BrokerName}', brokerName.value).replace('{Regulator}', regulatorName.value),
    },
  ],
}))

onMounted(() => {
  updateViewport()
  window.addEventListener('resize', updateViewport)
})

onBeforeUnmount(() => {
  cleanupPreviewScroll()
  window.removeEventListener('resize', updateViewport)
})
</script>

<template>
  <div class="flex h-screen items-center justify-center bg-[#0e0e0e] py-[102px]">
    <div class="carousel-license h-[calc(100vh-210px)]">
      <div class="carousel-big basic-core">
        <div v-if="false" class="left-btn" @click="prev()">
          <i class="iconfont icon-arrow-left"></i>
        </div>
        <div class="carouselList basic-core h-full text-center">
          <CommonImage
            id="zoomElement"
            :key="`${imgIndex}-${currentCarouselImage}`"
            class="carouselList-img m-auto h-full max-w-[1200px] object-contain md:max-w-[760px] lg:max-w-[920px]"
            style="-webkit-user-select: none; transition: background-color 300ms"
            :src="currentCarouselImage"
            alt="pic"
          />
        </div>
        <div v-if="false" class="right-btn" @click="next()">
          <i class="iconfont icon-arrow-right"></i>
        </div>
      </div>

      <div
        class="carousel-small-box relative mx-auto w-[1200px] md:w-[760px] lg:w-[920px]"
        :style="{ height: `${imgHeight}px` }"
      >
        <div
          v-if="hasThumbnailOverflow"
          class="prev absolute top-1/2 left-0 flex -translate-y-1/2 items-center rtl:right-0 rtl:left-[unset]"
          :style="{ height: `${imgHeight}px` }"
        >
          <div
            class="left-small-btn h-[24px] w-[24px] md:h-[16px] md:w-[16px] lg:h-[20px] lg:w-[20px]"
            @click.stop="prevImg()"
          >
            <i class="iconfont icon-arrow-down3 rtl:rotate-180"></i>
          </div>
        </div>

        <div
          v-if="!isMobile"
          class="carousel-small mt-[20px] w-[calc(1200px-72px)] md:w-[calc(760px-56px)] lg:w-[calc(920px-64px)]"
          :class="hasThumbnailOverflow ? 'mx-[36px] md:mx-[28px] lg:mx-[32px]' : 'mx-auto justify-center'"
          :style="{ height: `${imgHeight}px` }"
        >
          <div
            ref="imgBox"
            class="small-img-box space-x-[8px] rtl:space-x-reverse"
            :class="hasThumbnailOverflow ? '' : 'mx-auto !w-[unset]'"
            :style="{ height: `${imgHeight}px` }"
          >
            <div
              v-for="(item, index) in imageList"
              :key="index"
              class="carousel-small-img xy-cneter"
              :class="imgIndex === index ? 'isActivedImg' : ''"
              @click.stop="switchImg(index)"
            >
              <CommonImage
                :style="{
                  minWidth: `${ImgWidth}px`,
                  width: `${ImgWidth}px`,
                  height: `${imgHeight}px`,
                }"
                class="object-cover"
                :class="imgIndex === index ? '' : 'opacity-20'"
                :src="item"
                alt="pic"
              />

              <div
                class="mask"
                :style="{
                  minWidth: `${ImgWidth}px`,
                  width: `${ImgWidth}px`,
                  height: `${imgHeight}px`,
                }"
                :class="imgIndex === index ? 'mask-actived' : ''"
                @click="switchImg(index)"
              ></div>
            </div>
          </div>
        </div>

        <div
          v-if="hasThumbnailOverflow"
          class="next absolute top-1/2 right-0 flex h-[75px] -translate-y-1/2 items-center md:h-[45px] lg:h-[56px] rtl:right-[unset] rtl:left-0"
          :style="{ height: `${imgHeight}px` }"
        >
          <div
            class="right-small-btn h-[24px] w-[24px] md:h-[16px] md:w-[16px] lg:h-[20px] lg:w-[20px]"
            @click.stop="nextImg()"
          >
            <i class="iconfont icon-arrow-down2 rtl:rotate-180"></i>
          </div>
        </div>
      </div>
    </div>

    <ElDialog
      v-model="dialogVisible"
      :fullscreen="isMobile"
      width="100%"
      top="0vh"
      custom-class="image-preview-dialog"
      @close="dialogVisible = false"
    >
      <div @click="dialogVisible = false">
        <CommonImage
          ref="currentImageRef"
          :src="currentImage"
          style="
            display: block;
            -webkit-user-select: none;
            margin: auto;
            cursor: zoom-out;
            background-color: hsl(0, 0%, 90%);
            transition: background-color 300ms;
          "
          alt="preview-image"
          class="preview-image absolute top-0 right-0 left-0 max-w-screen"
        />
      </div>
    </ElDialog>
  </div>
</template>

<style src="~/assets/css/license/certificate.less" lang="less" scoped></style>

<style scoped lang="less">
:deep(.image-preview-dialog) {
  .el-dialog__body {
    max-height: unset !important;
    text-align: center !important;
    height: 100vh !important;
    background-color: #0e0e0e;
    display: flex;
    align-items: center;
    justify-content: center;

    &::-webkit-scrollbar {
      background-color: transparent;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 18px;
      transition: background 0.3s ease;
    }

    &.scrolling::-webkit-scrollbar-thumb {
      background: #878787;
      transition: background 0.3s ease;
    }
  }

  .el-dialog__header {
    display: none !important;
  }
}
</style>
